import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  Upload, Download, Package, DollarSign, Warehouse, 
  FileSpreadsheet, CheckCircle, XCircle, AlertCircle,
  RefreshCw, Eye, Trash2, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CSV Template generators
const generateInventoryTemplate = () => {
  return 'product_id,sku,current_stock,new_stock\n';
};

const generatePriceTemplate = () => {
  return 'product_id,sku,current_price,new_price,current_sale_price,new_sale_price\n';
};

const generateProductTemplate = () => {
  return `name,description,price,sale_price,stock,category,sku,weight,brand,fssai_number,ingredients,net_quantity,container_name,manufacturer,manufacturer_address,manufacturing_date,shelf_life,storage_instructions,country_of_origin,contact_details,image_url_1,image_url_2,image_url_3
"Sample Product","Product description",999,799,100,"Traditional Sweets","SKU-001",500,"Brand Name","12345678901234","Sugar, Ghee, Flour","500g","Box","Manufacturer Ltd","123 Street, City","2025-01-01","6 months","Store in cool dry place","India","contact@example.com","https://example.com/image1.jpg","",""\n`;
};

function CSVPreviewTable({ data, maxRows = 5 }) {
  if (!data || data.length === 0) return null;
  
  const headers = Object.keys(data[0]);
  const displayData = data.slice(0, maxRows);
  
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-100">
            {headers.map((header, i) => (
              <th key={i} className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayData.map((row, i) => (
            <tr key={i} className="border-b">
              {headers.map((header, j) => (
                <td key={j} className="px-3 py-2 whitespace-nowrap max-w-[200px] truncate">
                  {row[header] || '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length > maxRows && (
        <p className="text-sm text-gray-500 mt-2">
          Showing {maxRows} of {data.length} rows
        </p>
      )}
    </div>
  );
}

function BulkInventoryTab() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const blob = new Blob([generateInventoryTemplate()], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventory_update_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj = {};
      headers.forEach((header, i) => {
        obj[header] = values[i] || '';
      });
      return obj;
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = parseCSV(event.target.result);
        setPreview(data);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || preview.length === 0) {
      toast.error('Please select a valid CSV file');
      return;
    }

    setProcessing(true);
    try {
      const updates = preview.map(row => ({
        product_id: row.product_id,
        inventory: parseInt(row.new_stock) || 0
      }));

      const response = await axiosInstance.put('/api/vendor/products/bulk-inventory', {
        updates
      });

      setResults({
        success: response.data.updated || updates.length,
        failed: response.data.failed || 0,
        errors: response.data.errors || []
      });
      toast.success('Inventory updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update inventory');
      setResults({ success: 0, failed: preview.length, errors: [error.message] });
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview([]);
    setResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold">Bulk Inventory Update</h3>
          <p className="text-sm text-gray-600">Update stock quantities for multiple products at once</p>
        </div>
        <Button variant="outline" onClick={downloadTemplate}>
          <Download className="w-4 h-4 mr-2" /> Download Template
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label>Upload CSV File</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="mt-1"
              />
            </div>

            {preview.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Preview ({preview.length} rows)</h4>
                  <Button variant="ghost" size="sm" onClick={resetForm}>
                    <Trash2 className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </div>
                <CSVPreviewTable data={preview} />
              </div>
            )}

            {results && (
              <div className="p-4 rounded-lg bg-gray-50 space-y-2">
                <h4 className="font-medium">Results</h4>
                <div className="flex gap-4">
                  <Badge variant="default" className="bg-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" /> {results.success} Updated
                  </Badge>
                  {results.failed > 0 && (
                    <Badge variant="destructive">
                      <XCircle className="w-3 h-3 mr-1" /> {results.failed} Failed
                    </Badge>
                  )}
                </div>
                {results.errors?.length > 0 && (
                  <div className="text-sm text-red-600 mt-2">
                    {results.errors.map((err, i) => <p key={i}>{err}</p>)}
                  </div>
                )}
              </div>
            )}

            <Button 
              onClick={handleUpload} 
              disabled={processing || preview.length === 0}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Update Inventory</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BulkPriceTab() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const blob = new Blob([generatePriceTemplate()], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'price_update_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj = {};
      headers.forEach((header, i) => {
        obj[header] = values[i] || '';
      });
      return obj;
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = parseCSV(event.target.result);
        setPreview(data);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || preview.length === 0) {
      toast.error('Please select a valid CSV file');
      return;
    }

    setProcessing(true);
    try {
      const updates = preview.map(row => ({
        product_id: row.product_id,
        price: parseFloat(row.new_price) || 0,
        sale_price: row.new_sale_price ? parseFloat(row.new_sale_price) : null
      }));

      const response = await axiosInstance.put('/api/vendor/products/bulk-price', {
        updates
      });

      setResults({
        success: response.data.updated || updates.length,
        failed: response.data.failed || 0,
        errors: response.data.errors || []
      });
      toast.success('Prices updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update prices');
      setResults({ success: 0, failed: preview.length, errors: [error.message] });
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview([]);
    setResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold">Bulk Price Update</h3>
          <p className="text-sm text-gray-600">Update prices for multiple products at once</p>
        </div>
        <Button variant="outline" onClick={downloadTemplate}>
          <Download className="w-4 h-4 mr-2" /> Download Template
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label>Upload CSV File</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="mt-1"
              />
            </div>

            {preview.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Preview ({preview.length} rows)</h4>
                  <Button variant="ghost" size="sm" onClick={resetForm}>
                    <Trash2 className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </div>
                <CSVPreviewTable data={preview} />
              </div>
            )}

            {results && (
              <div className="p-4 rounded-lg bg-gray-50 space-y-2">
                <h4 className="font-medium">Results</h4>
                <div className="flex gap-4">
                  <Badge variant="default" className="bg-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" /> {results.success} Updated
                  </Badge>
                  {results.failed > 0 && (
                    <Badge variant="destructive">
                      <XCircle className="w-3 h-3 mr-1" /> {results.failed} Failed
                    </Badge>
                  )}
                </div>
              </div>
            )}

            <Button 
              onClick={handleUpload} 
              disabled={processing || preview.length === 0}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Update Prices</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BulkProductTab() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const blob = new Blob([generateProductTemplate()], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_product_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      // Handle quoted values with commas
      const values = [];
      let current = '';
      let inQuotes = false;
      for (let char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      const obj = {};
      headers.forEach((header, i) => {
        obj[header] = values[i]?.replace(/^"|"$/g, '') || '';
      });
      return obj;
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = parseCSV(event.target.result);
        setPreview(data);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || preview.length === 0) {
      toast.error('Please select a valid CSV file');
      return;
    }

    setProcessing(true);
    let successCount = 0;
    let failedCount = 0;
    const errors = [];

    try {
      for (const row of preview) {
        try {
          const images = [row.image_url_1, row.image_url_2, row.image_url_3].filter(Boolean);
          
          const productData = {
            name: row.name,
            description: row.description,
            price: parseFloat(row.price) || 0,
            sale_price: row.sale_price ? parseFloat(row.sale_price) : null,
            inventory: parseInt(row.stock) || 0,
            category_id: row.category,
            sku: row.sku,
            weight: parseFloat(row.weight) || 0,
            images: images,
            brand: row.brand || null,
            fssai_number: row.fssai_number || null,
            ingredients: row.ingredients || null,
            net_quantity: row.net_quantity || null,
            container_name: row.container_name || null,
            manufacturer: row.manufacturer || null,
            manufacturer_address: row.manufacturer_address || null,
            manufacturing_date: row.manufacturing_date || null,
            shelf_life: row.shelf_life || null,
            storage_instructions: row.storage_instructions || null,
            country_of_origin: row.country_of_origin || 'India',
            contact_details: row.contact_details || null
          };

          await axiosInstance.post('/api/vendor/products', productData);
          successCount++;
        } catch (err) {
          failedCount++;
          errors.push(`${row.name}: ${err.response?.data?.detail || err.message}`);
        }
      }

      setResults({ success: successCount, failed: failedCount, errors });
      if (successCount > 0) {
        toast.success(`Created ${successCount} products!`);
      }
    } catch (error) {
      toast.error('Bulk upload failed');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview([]);
    setResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold">Bulk Product Creation</h3>
          <p className="text-sm text-gray-600">Create multiple products at once via CSV upload</p>
        </div>
        <Button variant="outline" onClick={downloadTemplate}>
          <Download className="w-4 h-4 mr-2" /> Download Template
        </Button>
      </div>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <div className="flex gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Template includes extended fields:</p>
              <p>Brand, FSSAI, Ingredients, Net Quantity, Container, Manufacturer info, Shelf Life, Storage Instructions</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label>Upload CSV File</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="mt-1"
              />
            </div>

            {preview.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Preview ({preview.length} products)</h4>
                  <Button variant="ghost" size="sm" onClick={resetForm}>
                    <Trash2 className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </div>
                <CSVPreviewTable data={preview} />
              </div>
            )}

            {results && (
              <div className="p-4 rounded-lg bg-gray-50 space-y-2">
                <h4 className="font-medium">Results</h4>
                <div className="flex gap-4">
                  <Badge variant="default" className="bg-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" /> {results.success} Created
                  </Badge>
                  {results.failed > 0 && (
                    <Badge variant="destructive">
                      <XCircle className="w-3 h-3 mr-1" /> {results.failed} Failed
                    </Badge>
                  )}
                </div>
                {results.errors?.length > 0 && (
                  <div className="text-sm text-red-600 mt-2 max-h-32 overflow-y-auto">
                    {results.errors.map((err, i) => <p key={i}>{err}</p>)}
                  </div>
                )}
              </div>
            )}

            <Button 
              onClick={handleUpload} 
              disabled={processing || preview.length === 0}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating Products...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Create Products</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function VendorBulkOperations() {
  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Link to="/vendor/dashboard">
          <Button variant="ghost" size="icon" className="h-10 w-10">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bulk Operations</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage products in bulk using CSV uploads</p>
        </div>
      </div>

      <Tabs defaultValue="inventory" className="space-y-6">
        <TabsList className="bg-white border p-1 rounded-xl shadow-sm">
          <TabsTrigger value="inventory" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            <Warehouse className="w-4 h-4 mr-2" /> Inventory
          </TabsTrigger>
          <TabsTrigger value="price" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            <DollarSign className="w-4 h-4 mr-2" /> Prices
          </TabsTrigger>
          <TabsTrigger value="products" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
            <Package className="w-4 h-4 mr-2" /> New Products
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inventory">
          <Card className="bg-white shadow-sm">
            <CardContent className="pt-6">
              <BulkInventoryTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="price">
          <Card className="bg-white shadow-sm">
            <CardContent className="pt-6">
              <BulkPriceTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products">
          <Card className="bg-white shadow-sm">
            <CardContent className="pt-6">
              <BulkProductTab />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
